'use client';

// only for Next.js
import { c as _c } from "react/compiler-runtime";
import { useUploadFile, useUploadFiles } from '@better-upload/client';
import { UploadDropzone } from '@/components/ui/upload-dropzone';
import { UploadButton } from '@/components/ui/upload-button';
import { getApiBaseUrl } from '@/lib/api';
import Compressor from 'compressorjs';
import { PasteUploadArea } from './ui/paste-upload-area';
type BetterUploaderProps = {
  onUploadComplete?: (data: any) => void;
  onUploadProgress?: (data: any) => void;
  mode: 'multiple' | 'single';
  accept?: string;
  maxFiles?: number;
  maxFileSize?: string;
  fileTypes?: string;
  compress?: boolean; // Enable image compression
  compressionQuality?: number; // Compression quality (0-1), default 0.8
};

// Helper function to compress image files
async function compressImage(file: File, quality: number = 0.8): Promise<File> {
  // Only compress image files
  if (!file.type.startsWith('image/')) {
    return file;
  }
  return new Promise(resolve => {
    new Compressor(file, {
      quality,
      maxWidth: 2048,
      maxHeight: 2048,
      mimeType: 'image/jpeg',
      convertSize: 1000000,
      // Convert to JPEG if larger than 1MB
      success(result) {
        // Convert Blob to File
        const compressedFile = new File([result], file.name, {
          type: result.type,
          lastModified: Date.now()
        });
        resolve(compressedFile);
      },
      error(err) {
        console.error('Compression error:', err);
        // Return original file if compression fails
        resolve(file);
      }
    });
  });
}
export default function BetterUploader(props) {
  const $ = _c(10);
  let t0;
  if ($[0] !== props.accept || $[1] !== props.compress || $[2] !== props.compressionQuality || $[3] !== props.fileTypes || $[4] !== props.maxFileSize || $[5] !== props.maxFiles || $[6] !== props.mode || $[7] !== props.onUploadComplete || $[8] !== props.onUploadProgress) {
    t0 = props.mode === "multiple" ? <DropZone accept={props.accept} maxFiles={props.maxFiles} maxFileSize={props.maxFileSize} fileTypes={props.fileTypes} compress={props.compress} compressionQuality={props.compressionQuality} onUploadComplete={props.onUploadComplete} onUploadProgress={props.onUploadProgress} /> : <Single accept={props.accept} compress={props.compress} compressionQuality={props.compressionQuality} onUploadComplete={props.onUploadComplete} onUploadProgress={props.onUploadProgress} />;
    $[0] = props.accept;
    $[1] = props.compress;
    $[2] = props.compressionQuality;
    $[3] = props.fileTypes;
    $[4] = props.maxFileSize;
    $[5] = props.maxFiles;
    $[6] = props.mode;
    $[7] = props.onUploadComplete;
    $[8] = props.onUploadProgress;
    $[9] = t0;
  } else {
    t0 = $[9];
  }
  return t0;
}
function DropZone(props) {
  const $ = _c(17);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiBaseUrl();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.compress || $[2] !== props.compressionQuality || $[3] !== props.onUploadComplete || $[4] !== props.onUploadProgress) {
    t1 = {
      route: "files",
      credentials: "include",
      api: `${t0}better-uploads/upload`,
      onUploadComplete: props.onUploadComplete,
      onUploadProgress: props.onUploadProgress,
      async onBeforeUpload(data) {
        if (!props.compress) {
          return data.files;
        }
        const compressedFiles = await Promise.all(data.files.map(file => compressImage(file, props.compressionQuality)));
        return compressedFiles;
      }
    };
    $[1] = props.compress;
    $[2] = props.compressionQuality;
    $[3] = props.onUploadComplete;
    $[4] = props.onUploadProgress;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    control
  } = useUploadFiles(t1);
  let t2;
  if ($[6] !== props.fileTypes || $[7] !== props.maxFileSize || $[8] !== props.maxFiles) {
    t2 = {
      maxFiles: props.maxFiles,
      maxFileSize: props.maxFileSize,
      fileTypes: props.fileTypes
    };
    $[6] = props.fileTypes;
    $[7] = props.maxFileSize;
    $[8] = props.maxFiles;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== control || $[11] !== props.accept || $[12] !== t2) {
    t3 = <UploadDropzone control={control} accept={props.accept} description={t2} />;
    $[10] = control;
    $[11] = props.accept;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== control || $[15] !== t3) {
    t4 = <PasteUploadArea control={control}>{t3}</PasteUploadArea>;
    $[14] = control;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
function Single(props) {
  const $ = _c(12);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getApiBaseUrl();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.compress || $[2] !== props.compressionQuality || $[3] !== props.onUploadComplete || $[4] !== props.onUploadProgress) {
    t1 = {
      route: "files",
      credentials: "include",
      api: `${t0}better-uploads/upload`,
      onUploadComplete: props.onUploadComplete,
      onUploadProgress: props.onUploadProgress,
      async onBeforeUpload(data) {
        if (!props.compress) {
          return data.files;
        }
        const compressedFiles = await Promise.all(data.files.map(file => compressImage(file, props.compressionQuality)));
        return compressedFiles;
      }
    };
    $[1] = props.compress;
    $[2] = props.compressionQuality;
    $[3] = props.onUploadComplete;
    $[4] = props.onUploadProgress;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    control
  } = useUploadFiles(t1);
  let t2;
  if ($[6] !== control || $[7] !== props.accept) {
    t2 = <UploadButton control={control} accept={props.accept} />;
    $[6] = control;
    $[7] = props.accept;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== control || $[10] !== t2) {
    t3 = <PasteUploadArea control={control}>{t2}</PasteUploadArea>;
    $[9] = control;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}